// boardWorklist — what needs the board-runner right now, ranked.
//
// Pure. The clock and the derived state arrive as arguments.
//
// THREE TIERS, EACH NAMED BY A WORD, not by a colour:
//
//   ALARM    a room is unsafe now, or an emergency is unanswered
//   ACT NOW  a commitment already made that is decaying — a break waiting,
//            a relief staged with no handover, cover ending shortly
//   PLAN     a shortfall you can still absorb — the next boundary, someone
//            unplaced
//
// ⚠️ TIME ONLY BREAKS TIES *INSIDE* A TIER. A nine-minute uncovered room
// outranks a forty-one-minute break wait, because severity is not a function of
// how long something has been true. Sorting the whole list by age would put the
// oldest nuisance above the newest emergency.

export const TIERS = ["alarm", "act", "plan"];

export const TIER_LABEL = { alarm: "Alarm", act: "Act now", plan: "Plan" };

// Cover ending within this window is a commitment that is decaying rather than
// a future you can absorb — it moves up a tier.
export const COVER_SOON_MIN = 120;

function push(list, tier, key, glyph, text, detail, age) {
  list.push({ tier, key, glyph, text, detail, age: Number.isFinite(age) ? age : 0 });
}

/**
 * @param {object} ctx
 * @param {number} ctx.nowMin
 * @param {Array}  ctx.emergencies    [{ roomId, roomName, acknowledgedBy, ageMin }]
 * @param {Array}  ctx.uncoveredRooms [{ roomId, roomName }]
 * @param {Array}  ctx.breaksWaiting  [{ roomName, providerName, ageMin }]
 * @param {Array}  ctx.reliefRows     from stagedReliefRows
 * @param {Array}  ctx.lanes          for cover-ending-soon
 * @param {Array}  ctx.bench          unplaced people
 * @param {object} ctx.boundary       from nextBoundary
 * @param {function} ctx.coverageOf   lane => laneCoverage(lane)
 */
export function buildWorklist(ctx = {}) {
  const {
    nowMin,
    emergencies = [],
    uncoveredRooms = [],
    breaksWaiting = [],
    reliefRows = [],
    lanes = [],
    bench = [],
    boundary = null,
    coverageOf,
  } = ctx;

  const out = [];

  // ── ALARM ──────────────────────────────────────────────────────────────────
  for (const e of emergencies) {
    const acked = Array.isArray(e.acknowledgedBy) && e.acknowledgedBy.length > 0;
    if (acked) continue;
    push(out, "alarm", `em-${e.roomId || e.id}`, "!",
      `Emergency unanswered in ${e.roomName || "a room"}`,
      "Nobody has acknowledged it.", e.ageMin);
  }
  for (const r of uncoveredRooms) {
    push(out, "alarm", `unc-${r.roomId}`, "∅",
      `${r.roomName} has nobody covering it`,
      "The room is running without an assigned provider.", r.ageMin);
  }

  // ── ACT NOW ────────────────────────────────────────────────────────────────
  for (const b of breaksWaiting) {
    push(out, "act", `brk-${b.roomName}-${b.providerName}`, "●",
      `${b.providerName || "Someone"} is waiting for a break`,
      b.roomName ? `In ${b.roomName}.` : "", b.ageMin);
  }
  for (const r of reliefRows) {
    if (!r.stale) continue;
    push(out, "act", `rel-${r.caseId}`, "⇄",
      `Relief staged in ${r.roomName} has not happened`,
      r.staleReason === "departed"
        ? `${r.outgoingName} has already left.`
        : `${r.outgoingName} to ${r.incomingName}, staged over 30 minutes ago.`,
      0);
  }
  if (typeof coverageOf === "function") {
    for (const lane of lanes) {
      const cov = coverageOf(lane);
      if (!cov || !cov.uncovered) continue;
      // Already reported as an alarm if the room is uncovered NOW; this is the
      // one that has not happened yet but will.
      if (uncoveredRooms.some((r) => r.roomId === lane.roomId)) continue;
      const until = Number.isFinite(cov.departMin) && Number.isFinite(nowMin)
        ? cov.departMin - nowMin
        : null;
      const tier = until != null && until <= COVER_SOON_MIN ? "act" : "plan";
      push(out, tier, `cov-${lane.roomId}`, "▲",
        `${lane.roomName} loses cover before the case ends`,
        `${cov.shortfallMin} minutes short.`, until == null ? 0 : -until);
    }
  }

  // ── PLAN ───────────────────────────────────────────────────────────────────
  if (boundary && boundary.delta < 0) {
    push(out, "plan", "bound", "▲",
      `Short ${Math.abs(boundary.delta)} at ${boundary.label}`,
      `${boundary.crnas} available for ${boundary.rooms} rooms.`, 0);
  }
  for (const p of bench) {
    push(out, "plan", `bench-${p.id}`, "◦",
      `${p.name} is not placed`,
      "On the bench, available now.", 0);
  }

  const rank = (t) => TIERS.indexOf(t);
  return out.sort((a, b) => (rank(a.tier) - rank(b.tier)) || (b.age - a.age));
}

// What the board is watching on your behalf while nothing needs you. The empty
// state must read as SUPERVISED, not as a failed fetch — a blank panel and a
// working panel with nothing to report look identical otherwise.
export function watchingSummary({ roomsRunning = 0, boundary = null, bench = 0 } = {}) {
  const lines = [
    `${roomsRunning} ${roomsRunning === 1 ? "room" : "rooms"} running`,
    boundary ? `staffing at ${boundary.label}` : "no boundary left today",
    `${bench} free to place`,
    "every break channel",
  ];
  return lines;
}
